import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TenantContextService } from '../auth/tenant-context.service';
import { KeycloakService } from '../auth/keycloak.service';
import { environment } from '../../environments/environment';
import { RiskService, ManualVerificationTicket, PaginatedResponse } from '../views/risk/risk-service';
import { UserCacheService } from './user-cache.service';

export interface NewVerificationNotification {
    ref_code: string;
    ticket_id: string;
    user_id: string;
    created_at: string;
}

export interface VerificationExpiredNotification {
    verification_id: string;
    ticket_id: number;
    ref_code: string;
    user_id: string;
    event_type: 'ticket.manual.operator_rejected' | 'ticket.manual.expired';
    reason: string;
}

export interface WebSocketMessage {
    id: string;
    tenant_id: string;
    type: string;
    payload: {
        [key: string]: any;
    };
    timestamp: string;
}

export interface GlobalToastMessage {
    title: string;
    message: string;
    color?: string;
    autohide?: boolean;
    delay?: number;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private ws?: WebSocket;
    private isConnected$ = new BehaviorSubject<boolean>(false);
    private newVerificationNotification$ = new Subject<NewVerificationNotification>();
    private globalToast$ = new Subject<GlobalToastMessage>();
    private notificationsMuted$ = new BehaviorSubject<boolean>(false);
    private beepFrequencyMs$ = new BehaviorSubject<number>(5000); // Default 5 seconds
    private pendingTicketCount$ = new BehaviorSubject<number>(0);
    private pendingTickets$ = new BehaviorSubject<ManualVerificationTicket[]>([]);
    private audio = new Audio();
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private periodicSoundInterval?: any;
    private isLoadingTickets = false;

    readonly WS_URL = environment.websocketGwUrl;

    constructor(
        private tenantContextService: TenantContextService,
        private keycloakService: KeycloakService,
        private riskService: RiskService,
        private userCacheService: UserCacheService
    ) {
        this.loadMuteSetting();
        this.loadBeepFrequencySetting();
    }

    /**
     * Initialize WebSocket connection (called from app initialization)
     */
    init(): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }
        // Only initialize WebSocket and load tickets if user has 'risk' role
        if (this.keycloakService.hasRole('risk')) {
            this.loadPendingTickets();
            this.connect();
        }
    }

    /**
     * Get connection status observable
     */
    getConnectionStatus$(): Observable<boolean> {
        return this.isConnected$.asObservable();
    }

    /**
     * Get new verification notifications observable
     */
    getNewVerificationNotification$(): Observable<NewVerificationNotification> {
        return this.newVerificationNotification$.asObservable();
    }

    getGlobalToast$(): Observable<GlobalToastMessage> {
        return this.globalToast$.asObservable();
    }

    showGlobalToast(toast: GlobalToastMessage): void {
        this.globalToast$.next(toast);
    }

    /**
     * Get pending ticket count observable
     */
    getPendingTicketCount$(): Observable<number> {
        return this.pendingTicketCount$.asObservable();
    }

    /**
     * Get current pending ticket count
     */
    getPendingTicketCount(): number {
        return this.pendingTicketCount$.value;
    }

    /**
     * Increment pending ticket count and start periodic sound
     */
    incrementPendingTicketCount(): void {
        const currentCount = this.pendingTicketCount$.value;
        this.pendingTicketCount$.next(currentCount + 1);
        this.startPeriodicSound();
    }

    /**
     * Decrement pending ticket count and stop periodic sound if count reaches 0
     */
    decrementPendingTicketCount(): void {
        const currentCount = this.pendingTicketCount$.value;
        if (currentCount > 0) {
            this.pendingTicketCount$.next(currentCount - 1);
            if (currentCount - 1 === 0) {
                this.stopPeriodicSound();
            }
        }
    }

    /**
     * Reset pending ticket count
     */
    resetPendingTicketCount(): void {
        this.pendingTicketCount$.next(0);
        this.stopPeriodicSound();
    }

    /**
     * Check if notifications are muted
     */
    isMuted(): boolean {
        return this.notificationsMuted$.value;
    }

    /**
     * Toggle notification mute
     */
    toggleMute(): void {
        const newValue = !this.notificationsMuted$.value;
        this.notificationsMuted$.next(newValue);
        localStorage.setItem('ticketNotifications.muted', String(newValue));
    }

    /**
     * Get mute status observable
     */
    getMuteStatus$(): Observable<boolean> {
        return this.notificationsMuted$.asObservable();
    }

    /**
     * Get beep frequency in milliseconds observable
     */
    getBeepFrequency$(): Observable<number> {
        return this.beepFrequencyMs$.asObservable();
    }

    /**
     * Get current beep frequency in milliseconds
     */
    getBeepFrequencyMs(): number {
        return this.beepFrequencyMs$.value;
    }

    /**
     * Set beep frequency in milliseconds
     */
    setBeepFrequency(frequencyMs: number): void {
        this.beepFrequencyMs$.next(frequencyMs);
        localStorage.setItem('ticketNotifications.beepFrequency', String(frequencyMs));
        // Restart periodic sound with new frequency if currently playing
        if (this.periodicSoundInterval && this.pendingTicketCount$.value > 0) {
            this.stopPeriodicSound();
            this.startPeriodicSound();
        }
    }

    /**
     * Get pending tickets observable
     */
    getPendingTickets$(): Observable<ManualVerificationTicket[]> {
        return this.pendingTickets$.asObservable();
    }

    /**
     * Load pending tickets from API and update the count
     * Shows all tickets in the list, but only counts tickets with 'pending' or 'pending_operator' status for badge and sounds
     * Only loads if user has 'risk' role
     * Also caches user data from 360 API if not already cached
     */
    private loadPendingTickets(): void {
        // Only load tickets if user has 'risk' role
        if (!this.keycloakService.hasRole('risk')) {
            return;
        }

        if (this.isLoadingTickets) {
            return; // Prevent concurrent requests
        }

        this.isLoadingTickets = true;

        // Load first page with large per_page to get all verification tickets
        this.riskService.getTicketsForManualVerification(1, 1000).subscribe({
            next: (response: PaginatedResponse<ManualVerificationTicket>) => {
                const allTickets = response.data || [];

                // Get unique user IDs that need to be cached
                const userIdsToLoad = new Set<string>();
                allTickets.forEach(ticket => {
                    if (ticket.user_id && !this.userCacheService.hasUser(ticket.user_id)) {
                        userIdsToLoad.add(ticket.user_id);
                    }
                });

                // If there are users to load, load them all in parallel, then emit tickets
                if (userIdsToLoad.size > 0) {
                    const userDataRequests = Array.from(userIdsToLoad).map(userId =>
                        this.riskService.getPlayer360Data(userId, 'UNKNOWN').pipe(
                            map((player360Data) => {
                                if (player360Data && player360Data.player) {
                                    this.userCacheService.setUser(userId, {
                                        username: player360Data.player.username,
                                        email: player360Data.player.email,
                                        status: player360Data.player.status
                                    });
                                }
                                return true;
                            }),
                            catchError(() => {
                                console.warn(`Failed to load player 360 data for user ${userId}`);
                                return of(false);
                            })
                        )
                    );

                    forkJoin(userDataRequests).subscribe({
                        next: () => {
                            this.emitTicketsAndUpdateCounts(allTickets);
                            this.isLoadingTickets = false;
                        },
                        error: () => {
                            this.emitTicketsAndUpdateCounts(allTickets);
                            this.isLoadingTickets = false;
                        }
                    });
                } else {
                    // No users to load, emit tickets immediately
                    this.emitTicketsAndUpdateCounts(allTickets);
                    this.isLoadingTickets = false;
                }
            },
            error: (error) => {
                console.error('Failed to load pending tickets:', error);
                this.isLoadingTickets = false;
            }
        });
    }

    /**
     * Emit tickets and update pending count
     * @param allTickets All tickets to emit
     */
    private emitTicketsAndUpdateCounts(allTickets: ManualVerificationTicket[]): void {
        // Send ALL tickets to the list (both pending and claimed)
        this.pendingTickets$.next(allTickets);

        // Only count tickets with pending status for badge and sounds
        const pendingStatuses = ['pending', 'pending_operator'];
        const pendingCount = allTickets.filter(ticket =>
            pendingStatuses.includes(ticket.status.toLowerCase())
        ).length;

        const currentCount = this.pendingTicketCount$.value;

        if (pendingCount !== currentCount) {
            this.pendingTicketCount$.next(pendingCount);

            // Start or stop periodic sound based on pending count
            if (pendingCount > 0 && currentCount === 0) {
                this.startPeriodicSound();
            } else if (pendingCount === 0 && currentCount > 0) {
                this.stopPeriodicSound();
            }
        }
    }

    /**
     * Reload pending tickets (public method to trigger reload from components)
     */
    reloadPendingTickets(): void {
        this.loadPendingTickets();
    }

    /**
     * Clear the cached pending tickets immediately.
     * Useful when the UI should stop showing stale items before a refresh completes.
     */
    clearPendingTickets(): void {
        this.pendingTickets$.next([]);
        this.pendingTicketCount$.next(0);
        this.stopPeriodicSound();
    }

    /**
     * Check if incoming ticket should be auto-rejected based on auto-reject same configuration
     * This runs on ticket arrival via WebSocket, before it reaches manual verification queue
     * If auto-reject is triggered, the ticket is automatically rejected and won't appear in queue
     * 
     * @param ticketId - Ticket verification ID (used to submit rejection if needed)
     * @param refCode - Ticket reference code
     * @param userId - User ID for the ticket
     * @returns Observable<boolean> - Returns true if ticket was auto-rejected, false otherwise
     */
    private checkAndAutoRejectTicket(ticketId: string, refCode: string, userId: string): Observable<boolean> {
        return new Observable(observer => {
            // Get same tickets for this ref_code and user
            this.riskService.getSameTickets(refCode).subscribe({
                next: (sameTickets) => {
                    // If there are no same tickets, no need to check - skip auto-reject
                    if (!sameTickets || sameTickets.length === 0) {
                        observer.next(false); // No same tickets, not auto-rejected
                        observer.complete();
                        return;
                    }

                    // Extract ticket IDs from same tickets list
                    const ticketIds = sameTickets.map(t => t.ref_code);

                    // Check if this ticket should be auto-rejected
                    this.riskService.checkAutoRejectSame(ticketIds, userId).subscribe({
                        next: (response) => {
                            if (response.isRejected) {
                                // Auto-reject is enabled - submit rejection via API
                                const operatorId = this.keycloakService.getUserId();
                                const operatorName = this.keycloakService.getUsername();

                                const rejectionDecision: any = {
                                    decision: 'REJECT',
                                    operator_id: operatorId,
                                    operator_name: operatorName,
                                    reason: 'Auto-rejected: Same tickets from same user have been previously rejected'
                                };

                                // Submit rejection via API
                                this.riskService.submitManualVerificationDecision(ticketId, rejectionDecision).subscribe({
                                    next: () => {
                                        observer.next(true); // Was auto-rejected
                                        observer.complete();
                                    },
                                    error: (error) => {
                                        console.warn(`⚠ Failed to auto-reject ticket ${refCode}, but skipping notification:`, error?.status === 0 ? 'Network error' : error?.error?.message || error?.statusText);
                                        // On rejection submission error, don't show to user (was rejected but submission failed)
                                        observer.next(true); // Still consider it "handled" to avoid duplicate rejection
                                        observer.complete();
                                    }
                                });
                            } else {
                                // Ticket passed check - let it proceed to manual verification
                                observer.next(false); // Was NOT auto-rejected
                                observer.complete();
                            }
                        },
                        error: (error) => {
                            console.warn(`⚠ Error checking auto-reject for ticket ${refCode}, proceeding to manual verification:`, error?.status === 0 ? 'Network error' : error?.error?.message || error?.statusText);
                            // On error, let the ticket proceed normally to manual verification
                            observer.next(false); // Not auto-rejected due to error (fail-safe)
                            observer.complete();
                        }
                    });
                },
                error: (error) => {
                    console.warn(`⚠ Error fetching same tickets for ${refCode}, proceeding to manual verification:`, error?.status === 0 ? 'Network error' : error?.error?.message || error?.statusText);
                    // On error, let the ticket proceed normally to manual verification
                    observer.next(false); // Not auto-rejected due to error (fail-safe)
                    observer.complete();
                }
            });
        });
    }

    /**
     * Disconnect WebSocket
     */
    disconnect(): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.close();
        }
        this.isConnected$.next(false);
        this.stopPeriodicSound();
    }

    private connect(): void {
        try {
            const tenantId = this.tenantContextService.getTenantId();
            const wsUrl = `${this.WS_URL}?tenant_id=${encodeURIComponent(tenantId)}&app_id=admin-portal`;

            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                this.isConnected$.next(true);
                this.reconnectAttempts = 0;
            };

            this.ws.onmessage = (event) => {
                try {
                    const message: WebSocketMessage = JSON.parse(event.data);

                    // Check if this is a manual verification ticket creation and user has 'risk' role
                    if (message.type === 'manual_verification' && message.payload?.['event_type'] === 'ticket.manual.created') {
                        // Only process if user has 'risk' role
                        if (this.keycloakService.hasRole('risk')) {
                            const notification: NewVerificationNotification = {
                                ref_code: message.payload['ref_code'] || 'N/A',
                                ticket_id: message.payload['verification_id'],
                                user_id: message.payload['user_id'] || '',
                                created_at: message.timestamp || new Date().toISOString()
                            };

                            // Check if ticket should be auto-rejected BEFORE adding to manual verification queue
                            this.checkAndAutoRejectTicket(notification.ticket_id, notification.ref_code, notification.user_id).subscribe({
                                next: (wasAutoRejected: boolean) => {
                                    if (wasAutoRejected) {
                                        // Ticket was auto-rejected - don't notify admin, just reload list
                                        this.loadPendingTickets();
                                    } else {
                                        // Ticket passed check - notify admin for manual verification
                                        this.newVerificationNotification$.next(notification);
                                        this.playNotificationSound();
                                        this.loadPendingTickets();
                                    }
                                },
                                error: (error) => {
                                    console.error('Error checking auto-reject for ticket:', error);
                                    // Still proceed with normal flow on error (better to show than hide)
                                    this.newVerificationNotification$.next(notification);
                                    this.playNotificationSound();
                                    this.loadPendingTickets();
                                }
                            });
                        }
                    }

                    // Check if another admin resolved, rejected, or claimed a ticket
                    if (message.type === 'manual_verification' &&
                        (message.payload?.['event_type'] === 'ticket.manual.operator_rejected' ||
                            message.payload?.['event_type'] === 'ticket.manual.claimed')) {
                        // Reload tickets to reflect the change
                        if (this.keycloakService.hasRole('risk')) {
                            // Reload tickets to reflect the change
                            this.loadPendingTickets();
                        }
                    }
                    // Check if verification expired or operator rejected a ticket
                    if (message.type === 'manual_verification_player' &&
                        (message.payload?.['event_type'] === 'ticket.manual.player_action_required' ||
                            message.payload?.['event_type'] === 'ticket.manual.expired' ||
                            message.payload?.['event_type'] === 'ticket.manual.operator_rejected' ||
                            message.payload?.['event_type'] === 'ticket.manual.operator_accepted')) {
                        // Only process if user has 'risk' role
                        if (this.keycloakService.hasRole('risk')) {
                            // Reload tickets to reflect the change
                            this.loadPendingTickets();
                        }
                    }
                } catch (error) {
                    console.error('Failed to parse WebSocket message:', error);
                }
            };

            this.ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                this.isConnected$.next(false);
            };

            this.ws.onclose = () => {
                this.isConnected$.next(false);
                // Attempt to reconnect with exponential backoff
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    const backoffTime = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    this.reconnectAttempts++;
                    setTimeout(() => this.connect(), backoffTime);
                }
            };
        } catch (error) {
            console.error('Failed to initialize WebSocket:', error);
            this.isConnected$.next(false);
        }
    }

    private loadMuteSetting(): void {
        const mutedPreference = localStorage.getItem('ticketNotifications.muted');
        if (mutedPreference === 'true') {
            this.notificationsMuted$.next(true);
        }
    }

    private loadBeepFrequencySetting(): void {
        const frequencyStr = localStorage.getItem('ticketNotifications.beepFrequency');
        if (frequencyStr) {
            const frequency = parseInt(frequencyStr, 10);
            if (!isNaN(frequency) && frequency > 0) {
                this.beepFrequencyMs$.next(frequency);
            }
        } else {
            // Ensure default is set to 5000ms if not in localStorage
            this.beepFrequencyMs$.next(5000);
        }
    }

    private playNotificationSound(): void {
        if (this.isMuted()) {
            return;
        }

        try {
            // Use Web Audio API to generate a simple notification beep
            const audioContext = new (window as any).AudioContext || new (window as any).webkitAudioContext();
            const currentTime = audioContext.currentTime;

            // Create oscillator for the beep sound
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            // Set frequency and duration
            oscillator.frequency.value = 800; // Hz
            oscillator.type = 'sine';

            // Short beep (200ms)
            gainNode.gain.setValueAtTime(0.3, currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.2);

            oscillator.start(currentTime);
            oscillator.stop(currentTime + 0.2);
        } catch (error) {
            console.debug('Could not play notification sound via Web Audio:', error);
            // Fallback to file-based audio if available
            try {
                this.audio.src = 'assets/sounds/notification.mp3';
                this.audio.volume = 0.3;
                this.audio.play().catch(err => {
                    console.debug('Could not play audio file:', err);
                });
            } catch (fileError) {
                console.debug('Notification sound error:', fileError);
            }
        }
    }

    /**
     * Start periodic sound playback while there are pending tickets
     */
    private startPeriodicSound(): void {
        // If already running, don't start again
        if (this.periodicSoundInterval) {
            return;
        }

        // Play sound immediately
        this.playShortBeep();

        // Then set interval for periodic playback
        this.periodicSoundInterval = setInterval(() => {
            if (this.pendingTicketCount$.value > 0) {
                this.playShortBeep();
            }
        }, this.beepFrequencyMs$.value);
    }

    /**
     * Stop periodic sound playback
     */
    private stopPeriodicSound(): void {
        if (this.periodicSoundInterval) {
            clearInterval(this.periodicSoundInterval);
            this.periodicSoundInterval = undefined;
        }
    }

    /**
     * Play a short beep sound (used for periodic notifications)
     */
    private playShortBeep(): void {
        if (this.isMuted()) {
            return;
        }

        try {
            const audioContext = new (window as any).AudioContext || new (window as any).webkitAudioContext();
            const currentTime = audioContext.currentTime;

            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 1000; // Hz (slightly higher pitch)
            oscillator.type = 'sine';

            // Short beep (100ms)
            gainNode.gain.setValueAtTime(0.2, currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.1);

            oscillator.start(currentTime);
            oscillator.stop(currentTime + 0.1);
        } catch (error) {
            console.debug('Could not play beep via Web Audio:', error);
        }
    }
}
